// Het rooster als herbruikbare laag (Joris, 25 sep: "Rooster gewoon zo houden maar
// splitsen en hergebruiken bij Nog in te plannen"). Deze hook laadt alles wat het
// rooster toont — personen, ingeplande taken, draaidagen, agenda — via de gedeelde
// planningcache, rekent de bezetting en blokken uit, en bevat alle schrijfacties
// (verslepen, toewijzen, blok verschuiven) met hun vangnetten. Het Rooster gebruikt
// hem zonder bak (overzicht + snelle correctie); Nog in te plannen mét bak (inplannen).
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { PointerSensor, pointerWithin, useSensor, useSensors } from '@dnd-kit/core';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import { computeDayLoad } from '../../utils/capacityCalc';
import { useCapacityRules } from '../../utils/useCapacityRules';
import { leesCache, werkCacheBij, lopendeLading, useTraagVerversen } from '../../utils/planningCache';
import {
  haalPersonen, haalOngepland, haalTakenRange, haalAgenda, leesAgendaCache, sleutels, taakGewijzigd,
} from '../../utils/planningBronnen';
import { agendaVenster, agendaMails } from '../../utils/agendaVooruit';
import { persoonLabels, gedeeldeEmails, sorteerPersonen } from '../CapacityOverview/capaciteitPersonen';
import {
  maakKolommen, kolomSjabloon, persoonBlokken, weekTotaal, isBalkTaak, urenVan,
  afwezigeDagen, afwezigAlsTaken, uitvoerdersPlan, pasTakenAan, agendaBlokTaken, weekendWerk,
} from './rooster';
import { schrijfVerzetting } from './verzetten';
import { ROLLEN, ROL_LABEL, OVERIG, rollenVan, primaireRol, heeftRol } from '../../utils/rollen';
import { haalDraaidagen, draaidagenUitCache, draaidagTakenPerPersoon, isZelfdeAfspraak } from '../../utils/draaidagenPlanning';
import {
  addDays, startOfDay, eindDatumVoor, formatISO, planTaak, werkdagenVoor, agendaVan, lokaleDatum,
  werkdagVerschil, schuifPeriode, verschuifNaar,
} from './plannerDatums';

// ─── Rollen ──────────────────────────────────────────────────────────────────

// De rol-definitie is gedeeld (utils/rollen): datagedreven uit de functie-velden, zodat
// ook Regisseurs, Cameramensen en Projectleiders een eigen groep en filter krijgen.
export const FUNCTION_GROUPS = ROLLEN.map(({ key, label }) => ({ key, label }));
export const GROEP_LABEL = ROL_LABEL;

/** Primaire rol (eerste functie) — de groep waarin iemand in het rooster staat. */
export const getPersonFunctionGroup = (person) => primaireRol(person);

export const isFreelancer = (p) => /Freelancer|Bedrijf/.test(Array.isArray(p?.type) ? p.type.join(', ') : String(p?.type || ''));

/** localStorage-sleutel van "Stagiairs tonen" — gedeeld door Rooster en Nog in te plannen. */
export const STAGIAIRS_SLEUTEL = 'roosterStagiairs';

/**
 * Stagiair: Personen-type "Stagiair" (zoals het Stagiairs-vinkje op Capaciteit) of een functie
 * met "stagiair" erin. Type en functie kunnen lijst (multipleSelects) of tekst zijn.
 */
export const isStagiair = (p) => {
  const tekst = (v) => (Array.isArray(v) ? v.join(', ') : String(v || ''));
  return /stagiair/i.test(tekst(p?.type)) || /stagiair/i.test(tekst(p?.function || p?.Functie));
};

// Eerst: staat de pointer boven een dagcel? Dan dáár. Anders geen doel (loslaten
// buiten het rooster annuleert) — voorheen pakte closestCenter altijd wel íets.
export const botsing = (args) => pointerWithin(args);

// ─── Laden via de gedeelde planningcache ─────────────────────────────────────
// Taken worden over 5 weken opgehaald, zodat 1/2/4 weken wisselen niet opnieuw laadt.
const takenVenster = (ws) => ({ startDate: formatISO(ws), endDate: formatISO(addDays(ws, 35)) });
// Agenda alleen voor het zichtbare venster (ma t/m zo); zelfde periode als Capaciteit → gedeeld.
// agendaVenster/agendaMails komen uit utils/agendaVooruit: de vooraf-lading gebruikt zo
// gegarandeerd dezelfde cachesleutel.
const ongeplandVan = (d) => d?.byProject || d?.byDeliverable || {};

/**
 * Engine-agenda → maps met e-mail in kleine letters. `onbekend` (e-mail → reden) houdt de
 * foutmarkering vast: mailboxen die niet gelezen konden worden (niet_gelezen), of — bij een
 * algemene `fout` in het antwoord — alle gevraagde mailboxen. Zonder die markering zou
 * "0 u meetings" als "vrij" gelezen worden.
 */
export function agendaNaarState(d, gevraagd = []) {
  const klein = (obj, leeg) => {
    const uit = {};
    Object.entries(obj || {}).forEach(([e, v]) => { uit[e.toLowerCase()] = v || leeg(); });
    return uit;
  };
  const onbekend = {};
  if (d?.fout) {
    const reden = typeof d.fout === 'string' ? d.fout : 'agenda niet gelezen';
    (gevraagd || []).forEach((e) => { onbekend[String(e).toLowerCase()] = reden; });
  }
  Object.entries(d?.niet_gelezen || {}).forEach(([e, v]) => { onbekend[String(e).toLowerCase()] = v || 'niet gelezen'; });
  return {
    uren: klein(d?.hours, () => ({})), events: klein(d?.events, () => []), afwezig: klein(d?.afwezig, () => ({})), onbekend,
  };
}

/** Agenda kon helemaal niet geladen worden (bv. 503): iedereen onbekend, niets als "vrij" tonen. */
export const agendaMislukt = (gevraagd = [], reden = 'agenda niet geladen') => ({
  uren: {}, events: {}, afwezig: {},
  onbekend: Object.fromEntries((gevraagd || []).map((e) => [String(e).toLowerCase(), reden])),
});

/** E-mail → reden omzetten naar persoon-id → reden (alleen personen met een eigen agenda). */
export function agendaOnbekendPerPersoon(persons, onbekend, gedeeld = new Set()) {
  const uit = {};
  (persons || []).forEach((p) => {
    const email = String(p?.email || '').trim().toLowerCase();
    if (email && !gedeeld.has(email) && onbekend?.[email]) uit[p.id] = onbekend[email];
  });
  return uit;
}

/**
 * Wat er voor dit venster al in de cache ligt — alleen compleet (personen, taken en
 * zo nodig de bak), anders null. Daarmee staat het rooster bij terugkeren meteen, zonder spinner.
 */
function roosterUitCache(ws, numWeken, metBak) {
  const personen = leesCache(sleutels.personen());
  const ongepland = metBak ? leesCache(sleutels.ongepland()) : { waarde: {} };
  if (!personen || !ongepland) return null;
  const persons = personen.waarde || [];
  const takenArgs = { personIds: persons.map(p => p.id), ...takenVenster(ws) };
  const taken = persons.length ? leesCache(sleutels.takenRange(takenArgs)) : { waarde: {} };
  if (!taken) return null;
  const mails = agendaMails(persons, gedeeldeEmails(persons));
  const agenda = leesAgendaCache({ emails: mails, ...agendaVenster(ws, numWeken) });
  return {
    persons,
    ongepland: ongeplandVan(ongepland.waarde),
    taken: taken.waarde || {},
    takenSleutel: persons.length ? sleutels.takenRange(takenArgs) : null,
    agenda: agenda ? agendaNaarState(agenda.waarde, mails) : null,
  };
}

const zonderTaak = (bak, id) => {
  const updated = { ...bak };
  Object.keys(updated).forEach(key => {
    updated[key] = { ...updated[key], tasks: updated[key].tasks.filter(t => t.id !== id) };
    if (updated[key].tasks.length === 0) delete updated[key];
  });
  return updated;
};

/**
 * @param weekStart  maandag van het venster
 * @param numWeken   1, 2 of 4
 * @param metBak     ook de nog-in-te-plannen-taken laden en toewijzen (Nog in te plannen)
 * @param onGewijzigd  na elke geslaagde schrijfactie (bv. het overzicht laten hertellen)
 */
export default function useRooster({ weekStart, numWeken, metBak = false, onGewijzigd } = {}) {
  // Terug naar deze tab: de vorige stand staat meteen (Joris, 25 sep), daarna stil verversen.
  const beginRef = useRef();
  if (beginRef.current === undefined) beginRef.current = roosterUitCache(weekStart, numWeken, metBak);
  const begin = beginRef.current;
  const gewijzigdRef = useRef(onGewijzigd);
  gewijzigdRef.current = onGewijzigd;

  const [unassignedTasks, setUnassignedTasks] = useState(() => begin?.ongepland || {});
  const [persons, setPersons] = useState(() => begin?.persons || []);
  const [calendarHours, setCalendarHours] = useState(() => begin?.agenda?.uren || {});   // { email(lowercase): { date: uren } }
  const [agendaLaden, setAgendaLaden] = useState(false);
  const [agendaEvents, setAgendaEvents] = useState(() => begin?.agenda?.events || {});     // { email: [ { date, subject, start_time, end_time, hours } ] }
  const [agendaAfwezig, setAgendaAfwezig] = useState(() => begin?.agenda?.afwezig || {});   // { email: { date: 'afwezig (agenda)' } }
  const [agendaOnbekend, setAgendaOnbekend] = useState(() => begin?.agenda?.onbekend || {}); // { email: reden } — niet gelezen
  const [agendaFout, setAgendaFout] = useState(false); // hele agenda niet geladen (bv. 503)
  const { rules, reload: herlaadRegels } = useCapacityRules();
  const [existingTasks, setExistingTasks] = useState(() => begin?.taken || {});
  const [loading, setLoading] = useState(() => !begin);
  const { bezig: verversend, volg: volgVerversen } = useTraagVerversen();
  const [speeltuin, setSpeeltuin] = useState(null); // { personId, startDatum, deliverableName, taken }
  const [activeTask, setActiveTask] = useState(null);
  const [overCell, setOverCell] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  // Rooster: alle kalenderdagen (weekenden smal/gearceerd); rekenen doen we over de werkdagen.
  const kolommen = useMemo(() => maakKolommen(weekStart, numWeken), [weekStart, numWeken]);
  const sjabloon = useMemo(() => kolomSjabloon(kolommen, numWeken), [kolommen, numWeken]);
  const dayISOs = useMemo(() => kolommen.filter(k => !k.weekend).map(k => k.iso), [kolommen]);

  // `forceer` (standaard: na een wijziging, Verversen) haalt vers op; bij binnenkomen en
  // bladeren mag de cache antwoorden en ververst hij stil.
  const takenSleutelRef = useRef(begin?.takenSleutel || null);
  const geladenVensterRef = useRef(begin ? formatISO(weekStart) : null);
  const vraagRef = useRef(0);
  const fetchData = useCallback(async ({ forceer = true } = {}) => {
    const vraag = ++vraagRef.current;
    const actueel = () => vraagRef.current === vraag;
    const bij = (fn) => (nieuw) => { if (actueel()) fn(nieuw); };
    const venster = takenVenster(weekStart);
    // Spinner alleen als er voor dit venster niets te tonen is; anders blijft de stand staan.
    if (geladenVensterRef.current !== venster.startDate && !roosterUitCache(weekStart, 1, metBak)) setLoading(true);
    try {
      const [ongepland, personen] = await Promise.all([
        metBak ? haalOngepland({ forceer, bijVerversing: bij(d => setUnassignedTasks(ongeplandVan(d))) }) : null,
        haalPersonen({ forceer, bijVerversing: bij(p => setPersons(p || [])) }),
      ]);
      if (!actueel()) return;
      if (metBak) setUnassignedTasks(ongeplandVan(ongepland));
      setPersons(personen || []);
      let taken = {};
      let takenSleutel = null;
      if (personen?.length > 0) {
        const args = { personIds: personen.map(p => p.id), ...venster };
        takenSleutel = sleutels.takenRange(args);
        taken = await haalTakenRange(args, {
          forceer,
          bijVerversing: bij((t) => { takenSleutelRef.current = takenSleutel; setExistingTasks(t || {}); }),
        });
        if (!actueel()) return;
      }
      takenSleutelRef.current = takenSleutel;
      geladenVensterRef.current = venster.startDate;
      setExistingTasks(taken || {});
      volgVerversen(Promise.all([
        metBak && lopendeLading(sleutels.ongepland()), lopendeLading(sleutels.personen()),
        takenSleutel && lopendeLading(takenSleutel),
      ].filter(Boolean)));
    } catch (error) {
      if (!actueel()) return;
      console.error('Error fetching planner data:', error);
      toast.error('Kon data niet laden');
    } finally {
      if (actueel()) setLoading(false);
    }
  }, [weekStart, metBak, volgVerversen]);

  useEffect(() => { fetchData({ forceer: false }); }, [fetchData]);

  // Eigen wijzigingen (optimistisch) ook in de cache zetten, zodat terugkeren naar deze
  // tab de stand ná je wijziging toont — met de oorspronkelijke leeftijd, dus nog geverifieerd.
  useEffect(() => { if (takenSleutelRef.current) werkCacheBij(takenSleutelRef.current, existingTasks); }, [existingTasks]);
  useEffect(() => { if (metBak && geladenVensterRef.current) werkCacheBij(sleutels.ongepland(), { byProject: unassignedTasks }); }, [metBak, unassignedTasks]);
  // Na (en vlak vóór) elke schrijfactie: de takenstand van de andere planningstabs vervalt;
  // die van dit scherm blijft staan (die houden we hierboven zelf bij).
  const markeerWijziging = useCallback(() => {
    taakGewijzigd({ behalve: [takenSleutelRef.current, sleutels.ongepland()] });
  }, []);
  // Na een geslaagde schrijfactie: ook de pagina laten weten dat er iets veranderde.
  const gelukt = useCallback(() => { markeerWijziging(); gewijzigdRef.current?.(); }, [markeerWijziging]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  // Gedeelde postbussen (freelance@…) zijn geen eigen agenda: niet opvragen, niet optellen.
  const gedeeldeMails = useMemo(() => gedeeldeEmails(persons), [persons]);

  // Draaidagen uit de draaidagenbron (niet uit de agenda): per crewlid een vast blok en
  // een volle dag in de bezetting (Joris, 25 sep: "ik zie niet wanneer iemand een draaidag heeft").
  const [draaidagen, setDraaidagen] = useState(() => draaidagenUitCache(formatISO(weekStart), formatISO(addDays(weekStart, 35))) || []);
  const [draaidagRonde, setDraaidagRonde] = useState(0);
  const draaidagVersRef = useRef(false);
  useEffect(() => {
    let actueel = true;
    const forceer = draaidagVersRef.current;
    draaidagVersRef.current = false;
    const vanaf = formatISO(weekStart);
    const tot = formatISO(addDays(weekStart, 35));
    const bewaard = !forceer && draaidagenUitCache(vanaf, tot);
    if (bewaard) setDraaidagen(bewaard);
    haalDraaidagen(vanaf, { tot, forceer })
      .then((d) => { if (actueel) setDraaidagen(d); })
      .catch((e) => { if (actueel) console.warn('[rooster] draaidagen niet geladen:', e.message); });
    return () => { actueel = false; };
  }, [weekStart, draaidagRonde]);
  const draaidagTaken = useMemo(() => draaidagTakenPerPersoon(draaidagen, persons, existingTasks, {
    van: formatISO(weekStart), tot: formatISO(addDays(weekStart, 35)),
  }), [draaidagen, persons, existingTasks, weekStart]);

  // Echte meeting-uren uit Outlook — op de achtergrond en alleen voor het zichtbare venster.
  // Via de gedeelde cache: zelfde venster als Capaciteit = zelfde sleutel, dus wisselen is direct.
  const [agendaRonde, setAgendaRonde] = useState(0);
  const agendaVersRef = useRef(false);
  useEffect(() => {
    const emails = agendaMails(persons, gedeeldeMails);
    if (!emails.length) return undefined;
    let actueel = true;
    const forceer = agendaVersRef.current;
    agendaVersRef.current = false;
    const args = { emails, ...agendaVenster(weekStart, numWeken) };
    const toon = ({ uren, events, afwezig, onbekend }, fout = false) => {
      if (!actueel) return;
      setCalendarHours(uren);
      setAgendaEvents(events);
      setAgendaAfwezig(afwezig);
      setAgendaOnbekend(onbekend);
      setAgendaFout(fout);
    };
    const zet = (d) => toon(agendaNaarState(d, emails));
    const bewaard = !forceer && leesAgendaCache(args);
    if (bewaard) zet(bewaard.waarde); else setAgendaLaden(true);
    // Stopt het doorvragen naar een verse agenda als dit venster/scherm weg is.
    const stopper = new AbortController();
    haalAgenda(args, { forceer, bijVerversing: zet, signaal: stopper.signal })
      .then((d) => { zet(d); if (actueel) volgVerversen(lopendeLading(sleutels.agenda(args))); })
      // Agenda niet geladen (engine 503, netwerk): geen melding per bladerklik, maar stil
      // "agenda onbekend" bij iedereen — en geen oude uren van een ander venster laten staan.
      // (De cache bewaart niets: haalAgenda gooit, dus het volgende bezoek probeert opnieuw.)
      .catch((e) => {
        console.warn('[rooster] agenda niet geladen:', e?.message);
        if (!bewaard) toon(agendaMislukt(emails), true);
      })
      .finally(() => { if (actueel) setAgendaLaden(false); });
    return () => { actueel = false; stopper.abort(); };
  }, [persons, gedeeldeMails, weekStart, numWeken, agendaRonde, volgVerversen]);
  const verversAlles = useCallback(() => {
    agendaVersRef.current = true;
    setAgendaRonde(n => n + 1);
    draaidagVersRef.current = true;
    setDraaidagRonde(n => n + 1);
    fetchData();
  }, [fetchData]);

  const herstelTaak = useCallback(async (taskId, vorige) => {
    const r = await authFetch(`${BACKEND_URL}/Api/tasks/${taskId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vorige),
    });
    if (!r.ok) throw new Error('Terugzetten mislukt');
    gelukt();
    await fetchData();
  }, [fetchData, gelukt]);

  // ── Personen: labels, belasting, blokken ─────────────────────────────────
  const labels = useMemo(() => persoonLabels(persons), [persons]);
  const agendaOnbekendPer = useMemo(
    () => agendaOnbekendPerPersoon(persons, agendaOnbekend, gedeeldeMails),
    [persons, agendaOnbekend, gedeeldeMails],
  );

  const belasting = useMemo(() => {
    const uit = {};
    persons.forEach((p) => {
      const email = String(p.email || '').trim().toLowerCase();
      const eigen = email && !gedeeldeMails.has(email);
      // Afwezig volgens de agenda (hele dag, of een "VAKANTIE"-afspraak van een hele dag) = vrije dag.
      const afwezig = eigen ? afwezigeDagen(agendaAfwezig[email], agendaEvents[email], rules.hoursPerDay) : [];
      // Draaidagen uit de bron tellen als volle dag (zoals een Draaidag-taak).
      const taken = [...(existingTasks[p.id] || []), ...(draaidagTaken[p.id] || []), ...afwezigAlsTaken(afwezig)];
      const load = computeDayLoad(taken, dayISOs, agendaVan(calendarHours, p, gedeeldeMails), rules);
      // Welk deel van de dag is projectwerk (de blokken)? De rest — agenda, vanallesuurtjes —
      // tekent de strook grijs gearceerd, zodat het oog naar de projecten gaat.
      const projectUren = computeDayLoad(taken.filter(t => isBalkTaak(t) && !t.bronDraaidag), dayISOs, {}, rules).uren;
      uit[p.id] = { load: { ...load, projectUren }, totaal: weekTotaal(load.uren, load.staat, dayISOs, rules.hoursPerDay), afwezig };
    });
    return uit;
  }, [persons, existingTasks, draaidagTaken, dayISOs, calendarHours, agendaEvents, agendaAfwezig, gedeeldeMails, rules]);

  // Alleen-lezen bezetting die niet uit de takenlijst komt (draaidagen uit de bron, afwezig
  // volgens de agenda) — voor de speeltuin, zodat die dezelfde capaciteit toont als het
  // rooster. Gescheiden van existingTasks: synthetische records worden nooit gepatcht.
  const vasteBezetting = useMemo(() => {
    const uit = {};
    persons.forEach((p) => {
      uit[p.id] = [...(draaidagTaken[p.id] || []), ...afwezigAlsTaken(belasting[p.id]?.afwezig)]
        .map((t) => ({ ...t, alleenLezen: true }));
    });
    return uit;
  }, [persons, draaidagTaken, belasting]);

  const agendaPerPersoon = useMemo(() => {
    const uit = {};
    persons.forEach((p) => {
      const email = String(p.email || '').trim().toLowerCase();
      if (!email || gedeeldeMails.has(email)) return;
      const perDag = {};
      (agendaEvents[email] || []).forEach((ev) => { (perDag[ev.date] = perDag[ev.date] || []).push(ev); });
      uit[p.id] = perDag;
    });
    return uit;
  }, [persons, agendaEvents, gedeeldeMails]);

  const blokkenPerPersoon = useMemo(() => {
    const uit = {};
    persons.forEach((p) => {
      const email = String(p.email || '').trim().toLowerCase();
      const eigenAgenda = email && !gedeeldeMails.has(email) ? agendaEvents[email] : [];
      const draaidag = draaidagTaken[p.id] || [];
      // Vaste blokken (niet sleepbaar, neutraal): draaidagen en afspraken van ≥ 3 u.
      const agendaBlokken = agendaBlokTaken(eigenAgenda, {
        afwezig: belasting[p.id]?.afwezig, draaidagen: draaidag, zelfde: isZelfdeAfspraak,
      });
      uit[p.id] = persoonBlokken([...(existingTasks[p.id] || []), ...draaidag, ...agendaBlokken], kolommen);
    });
    return uit;
  }, [persons, existingTasks, draaidagTaken, agendaEvents, gedeeldeMails, belasting, kolommen]);

  // Weekendwerk (agenda en draaidagen op za/zo): nooit op een werkdag, maar als stille
  // markering in de weekendkolom. Telt niet mee in de capaciteit (die rekent met werkdagen).
  const weekendPerPersoon = useMemo(() => {
    const uit = {};
    persons.forEach((p) => {
      const email = String(p.email || '').trim().toLowerCase();
      const eigenAgenda = email && !gedeeldeMails.has(email) ? agendaEvents[email] : [];
      const w = weekendWerk(eigenAgenda, draaidagTaken[p.id], kolommen, { zelfde: isZelfdeAfspraak });
      if (Object.keys(w).length) uit[p.id] = w;
    });
    return uit;
  }, [persons, agendaEvents, gedeeldeMails, draaidagTaken, kolommen]);

  // ── Schrijfacties ──────────────────────────────────────────────────────────

  /** Taak uit de bak aan iemand geven, vanaf `startDatum` (Date) over zijn werkdagen. */
  const wijsToe = useCallback(async (task, personId, { start, eind, melding }) => {
    const startISO = formatISO(start);
    const endISO = formatISO(eind);
    // "Ongedaan maken": terug naar de nog-in-te-plannen-lijst.
    const vorige = { Assignee: [], StartDateUitvoerder: task.startDate || null, EndDateUitvoerder: task.endDate || null, NogInTePlannen: true };
    markeerWijziging();
    setUnassignedTasks(prev => zonderTaak(prev, task.id));
    setExistingTasks(prev => ({
      ...prev,
      [personId]: [...(prev[personId] || []), { ...task, startDate: startISO, endDate: endISO, assigneeIds: [personId] }],
    }));
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Assignee: [personId], StartDateUitvoerder: startISO, EndDateUitvoerder: endISO, NogInTePlannen: false }),
      });
      if (!res.ok) throw new Error('Update failed');
      gelukt();
      biedOngedaanAan(melding, () => herstelTaak(task.id, vorige));
    } catch (error) {
      console.error('Error assigning task:', error);
      toast.error('Kon taak niet toewijzen');
      fetchData();
    }
  }, [markeerWijziging, gelukt, herstelTaak, fetchData]);

  /** Snel toewijzen: persoon geselecteerd → klik op een taak in de bak. Plant vanaf `vanaf` met de deadline in acht. */
  const snelToewijzen = useCallback((task, personId, vanaf) => {
    const naam = labels[personId] || '';
    const { start, eind, waarschuwing, verschoven } = planTaak(task, startOfDay(vanaf));
    const startISO = formatISO(start);
    const melding = waarschuwing
      ? `Taak toegewezen aan ${naam} (${startISO}) — ⚠️ ${waarschuwing}`
      : verschoven
        ? `Taak toegewezen aan ${naam} — naar voren gehaald (${startISO}) om de deadline te halen`
        : `Taak toegewezen aan ${naam} (${startISO})`;
    return wijsToe(task, personId, { start, eind, melding });
  }, [labels, wijsToe]);

  const verschuifBlok = async ({ tasks, bronPersoonId: bron }, { personId, date }) => {
    const eerste = tasks.map(t => String(t.startDate).slice(0, 10)).sort()[0];
    const delta = werkdagVerschil(eerste, date);
    const wisselt = bron !== personId;
    if (delta === 0 && !wisselt) return;
    const plan = tasks.map((t) => {
      const s = String(t.startDate).slice(0, 10);
      const e = String(t.endDate || t.startDate).slice(0, 10);
      const { origineel, nieuw } = uitvoerdersPlan(t, existingTasks, bron, personId);
      const { start, eind } = schuifPeriode(s, e, delta);
      return {
        t, origineel, nieuw, start, eind,
        vorige: { ...(wisselt ? { Assignee: origineel } : {}), StartDateUitvoerder: s, EndDateUitvoerder: e },
      };
    });
    // Onbekende uitvoerders: liever niets dan mede-uitvoerders wissen.
    if (wisselt && plan.some(p => !p.origineel)) {
      toast.error('Kan de uitvoerders van een of meer taken niet vaststellen — verplaatsen naar een collega afgebroken');
      return;
    }
    markeerWijziging();
    setExistingTasks(prev => pasTakenAan(prev, plan.map(p => ({
      task: p.t, van: p.origineel || [bron], naar: p.nieuw, startDate: p.start, endDate: p.eind, bekend: !!p.origineel,
    }))));
    // Wegschrijven via het gedeelde verzetpad (ook Capaciteit en de Dagstart gebruiken dit):
    // één melding met "Ongedaan maken" voor het hele blok, eerlijk bij een deels mislukte actie.
    const naam = labels[personId] || '';
    await schrijfVerzetting(plan.map(p => ({
      taak: p.t,
      velden: { ...(wisselt ? { Assignee: p.nieuw } : {}), StartDateUitvoerder: p.start, EndDateUitvoerder: p.eind },
      vorige: p.vorige,
    })), {
      melding: !wisselt
        ? `${plan.length} taken ${Math.abs(delta)} werkdag${Math.abs(delta) === 1 ? '' : 'en'} ${delta > 0 ? 'later' : 'eerder'} gezet`
        : `${plan.length} taken verplaatst naar ${naam}`,
      naGelukt: gelukt,
      herlaad: fetchData,
    });
  };

  /** Bestaande taak (van een persoonsrij) verschuiven of naar een andere collega. */
  const verplaatsTaak = async (task, personId, date) => {
    const bron = task.bronPersoonId;
    const naam = labels[personId] || '';
    const vorigeStart = String(task.startDate || '').slice(0, 10);
    const vorigeEind = String(task.endDate || task.startDate || '').slice(0, 10);
    if (bron === personId && vorigeStart === date) return; // niets veranderd
    // Zelfde verschuiving als een projectblok: eind schuift evenveel werkdagen mee;
    // alleen van persoon wisselen (zelfde start) laat het eind exact staan.
    const { eind } = verschuifNaar(vorigeStart, vorigeEind, date);
    // Mede-uitvoerders blijven staan: alleen de bron wordt vervangen, undo zet de hele lijst terug.
    const { wisselt, origineel, nieuw, afbreken } = uitvoerdersPlan(task, existingTasks, bron, personId);
    if (afbreken) {
      toast.error('Kan de uitvoerders van deze taak niet vaststellen — verplaatsen naar een collega afgebroken');
      return;
    }
    const vorige = {
      ...(wisselt ? { Assignee: origineel } : {}),
      StartDateUitvoerder: vorigeStart || null,
      EndDateUitvoerder: vorigeEind || null,
    };
    markeerWijziging();
    setExistingTasks(prev => pasTakenAan(prev, [{
      task, van: origineel || [bron], naar: nieuw, startDate: date, endDate: eind, bekend: !!origineel,
    }]));
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/tasks/${task.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...(wisselt ? { Assignee: nieuw } : {}), StartDateUitvoerder: date, EndDateUitvoerder: eind }),
      });
      if (!res.ok) throw new Error('Update failed');
      gelukt();
      biedOngedaanAan(!wisselt
        ? `Taak verschoven naar ${date}`
        : `Taak verplaatst naar ${naam} (${date})${nieuw.length > 1 ? ` — ${nieuw.length - 1} mede-uitvoerder${nieuw.length > 2 ? 's blijven' : ' blijft'}` : ''}`,
      () => herstelTaak(task.id, vorige));
    } catch {
      toast.error('Kon taak niet verplaatsen');
      fetchData();
    }
  };

  // ── Drag & drop ────────────────────────────────────────────────────────────
  const onDragStart = (event) => {
    const taskData = event.active.data.current;
    if (taskData?.type === 'task-bar') setActiveTask(taskData.task);
    else if (taskData?.type === 'blok') setActiveTask({ ...taskData.tasks[0], name: `${taskData.label || 'Project'} — ${taskData.tasks.length} taken`, hours: urenVan(taskData.tasks) });
    else if (taskData?.type === 'deliverable') setActiveTask({ name: `Deliverable: ${taskData.deliverable?.name}`, deliverable: true, hours: (taskData.tasks || []).reduce((s, t) => s + (Number(t.hours) || 0), 0) });
  };

  const onDragOver = (event) => {
    const { over } = event;
    if (over?.data?.current?.type === 'capacity-cell') setOverCell({ personId: over.data.current.personId, date: over.data.current.date });
    else setOverCell(null);
  };

  const onDragCancel = () => { setActiveTask(null); setOverCell(null); };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTask(null);
    setOverCell(null);
    if (!over || !active) return;
    const taskData = active.data.current;
    const dropData = over.data.current;
    if (dropData?.type !== 'capacity-cell') return;

    // Hele deliverable op een persoon: open de speeltuin-popup (Joris, 1 sep).
    if (taskData?.type === 'deliverable') {
      const taken = taskData.tasks || [];
      if (!taken.length) return;
      setSpeeltuin({ personId: dropData.personId, startDatum: dropData.date, deliverableName: taskData.deliverable?.name || 'Deliverable', taken });
      return;
    }
    // Projectblok met meerdere taken: alles evenveel werkdagen verschuiven (en eventueel
    // naar een collega). Eén "Ongedaan maken" zet alle taken terug.
    if (taskData?.type === 'blok') { await verschuifBlok(taskData, dropData); return; }
    if (taskData?.type !== 'task-bar') return;

    const task = taskData.task;
    const { personId, date } = dropData;
    if (task.bronPersoonId) { await verplaatsTaak(task, personId, date); return; }

    // Uit de bak: de gebruiker kiest zelf de dag; bij een deadline-overschrijding alleen waarschuwen.
    const naam = labels[personId] || '';
    const start = lokaleDatum(date);
    const eind = eindDatumVoor(start, werkdagenVoor(task));
    const deadline = task.deadline ? lokaleDatum(task.deadline) : null;
    const naDeadline = deadline && !isNaN(deadline) && eind > deadline;
    await wijsToe(task, personId, {
      start, eind,
      melding: naDeadline
        ? `Taak toegewezen aan ${naam} — loopt tot ${formatISO(eind)}, ⚠️ ná de deadline (${formatISO(deadline)})`
        : `Taak toegewezen aan ${naam}`,
    });
  };

  const speeltuinKlaar = useCallback(() => { setSpeeltuin(null); gelukt(); fetchData(); }, [gelukt, fetchData]);

  return {
    loading, verversend, agendaLaden, agendaFout, agendaOnbekend: agendaOnbekendPer, rules, herlaadRegels,
    persons, labels, existingTasks, unassignedTasks, calendarHours, gedeeldeMails, vasteBezetting,
    kolommen, sjabloon, dayISOs, belasting, blokkenPerPersoon, agendaPerPersoon, weekendPerPersoon,
    fetchData, verversAlles, snelToewijzen,
    speeltuin, sluitSpeeltuin: () => setSpeeltuin(null), speeltuinKlaar,
    dnd: { sensors, collisionDetection: botsing, onDragStart, onDragOver, onDragEnd, onDragCancel },
    activeTask, overCell,
  };
}

/**
 * Personen per rolgroep, gefilterd (zoekterm, freelancers, rollen) en gesorteerd.
 * Groep = primaire rol (eerste functie); de rolfilter kijkt naar álle functies. Staat er
 * een filter aan, dan staat iemand onder de eerste van zijn rollen die in de filter zit
 * ("Editor, Regisseur" bij filter Regisseurs → onder Regisseurs).
 * De geselecteerde collega blijft altijd zichtbaar en staat bovenaan.
 */
export function groepeerPersonen({ persons, labels, belasting, zoek = '', toonFreelancers = false, toonStagiairs = true, functionFilter = [], sortering = 'ruimte', selectedPerson = null }) {
  const persoonMatch = (p) => `${p.name || ''} ${labels[p.id] || ''}`.toLowerCase().includes(zoek);
  const zoektPersoon = !!zoek && persons.some(persoonMatch);
  const groups = Object.fromEntries([...ROLLEN.map(r => [r.key, []]), [OVERIG, []]]);
  persons.forEach((person) => {
    const gekozen = person.id === selectedPerson;
    if (!gekozen) {
      if (!toonFreelancers && isFreelancer(person)) return;
      // Stagiairs uit = niet in beeld, ongeacht hun rol (wiki Rooster 27 sep).
      if (!toonStagiairs && isStagiair(person)) return;
      if (zoektPersoon && !persoonMatch(person)) return;
      if (!heeftRol(person, functionFilter)) return;
    }
    const inFilter = functionFilter.length ? rollenVan(person).find(k => functionFilter.includes(k)) : null;
    groups[inFilter || primaireRol(person)].push(person);
  });
  const vrij = Object.fromEntries(persons.map(p => [p.id, belasting[p.id]?.totaal.ruimte || 0]));
  const uit = {};
  Object.entries(groups).forEach(([key, lijst]) => {
    if (!lijst.length) return;
    const volgorde = sorteerPersonen(lijst.map(p => p.id), sortering, labels, vrij);
    const perId = Object.fromEntries(lijst.map(p => [p.id, p]));
    uit[key] = volgorde.map(id => perId[id]).sort((a, b) => (b.id === selectedPerson) - (a.id === selectedPerson));
  });
  return { groepen: uit, zoektPersoon };
}
